import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, Instagram, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ImageCardSkeleton } from '../components/ui/loading-skeleton';
import axios from 'axios';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seo';
import { createVideoObjectSchema, createBreadcrumbSchema } from '../utils/seoSchemas';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VideosPage = () => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/videos`);
        setVideos(response.data.videos || []);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const filteredVideos = videos.filter(video => {
    const categoryMatch = categoryFilter === 'all' || video.category === categoryFilter;
    const typeMatch = typeFilter === 'all' || video.type === typeFilter;
    return categoryMatch && typeMatch;
  });

  // Fonction pour déterminer l'aspect ratio selon le format
  const getAspectClass = (format) => {
    switch(format) {
      case 'vertical':
        return 'aspect-[9/16]'; // Format vertical 9:16
      case 'story':
        return 'aspect-square'; // Format story 1:1
      case 'horizontal':
      default:
        return 'aspect-video'; // Format horizontal 16:9 (défaut)
    }
  };

  const getVideoLink = (video) => video.instagram_url || video.youtube_url || '';
  const isInstagram = (video) => !!video.instagram_url;

  // Video schemas for SEO
  const videosSchema = videos.length > 0 ? {
    "@context": "https://schema.org",
    "@graph": filteredVideos.slice(0, 10).map(video => createVideoObjectSchema(video))
  } : null;

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: "Accueil", path: "/" },
    { name: "Vidéos", path: "/videos" }
  ]);

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-32" data-testid="videos-page">
      <SEO 
        title={seoConfig.videos.title}
        description={seoConfig.videos.description}
        keywords={seoConfig.videos.keywords}
        canonical={seoConfig.videos.canonical}
        structuredData={videosSchema || breadcrumbSchema}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl sm:text-6xl tracking-tight text-[#F5F0E8] mb-3"
          >
            VIDÉOS
          </motion.h1>
          <p className="font-serif italic text-lg text-[#B9B2A8]">
            {t('videos.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[200px] bg-white/5 border-white/10 text-[#F5F0E8]">
              <SelectValue placeholder={t('videos.type')} />
            </SelectTrigger>
            <SelectContent className="bg-[#0B0B0C] border-white/10">
              <SelectItem value="all" className="text-[#F5F0E8]">{t('videos.all_types')}</SelectItem>
              <SelectItem value="aftermovie" className="text-[#F5F0E8]">{t('videos.aftermovie')}</SelectItem>
              <SelectItem value="teaser" className="text-[#F5F0E8]">{t('videos.teaser')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-[200px] bg-white/5 border-white/10 text-[#F5F0E8]">
              <SelectValue placeholder={t('videos.category')} />
            </SelectTrigger>
            <SelectContent className="bg-[#0B0B0C] border-white/10">
              <SelectItem value="all" className="text-[#F5F0E8]">{t('videos.all_categories')}</SelectItem>
              <SelectItem value="openair" className="text-[#F5F0E8]">{t('videos.openair')}</SelectItem>
              <SelectItem value="mirano" className="text-[#F5F0E8]">{t('videos.mirano')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading Skeletons */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <ImageCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Videos Grid */}
        {!loading && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVideos.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <a
                href={getVideoLink(video)}
                target="_blank"
                rel="noopener noreferrer"
                className={`block w-full group relative ${getAspectClass(video.format)} rounded-2xl overflow-hidden bg-white/5 border border-white/10 card-hover`}
                data-testid={`video-card-${video.id}`}
                aria-label={`${video.title} — ouvrir sur ${isInstagram(video) ? 'Instagram' : 'YouTube'}`}
              >
                <img
                  src={getImageUrl(video.thumbnail)}
                  alt={`${video.title} - Invasion Latina ${video.category === 'openair' ? 'Open Air' : 'Mirano'} ${video.type === 'aftermovie' ? 'Aftermovie' : 'Teaser'}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-[#C9A84C] flex items-center justify-center transform transition-all duration-300 group-hover:scale-110 group-hover:bg-[#E2C56A]">
                    <Play className="w-8 h-8 text-[#060606] ml-1" fill="currentColor" />
                  </div>
                </div>
                {isInstagram(video) && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white text-xs">
                    <Instagram className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Voir sur Instagram</span>
                    <ExternalLink className="w-3 h-3 opacity-70" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm">
                      {video.type === 'aftermovie' ? t('videos.aftermovie') : t('videos.teaser')}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm">
                      {video.category === 'openair' ? t('videos.openair') : t('videos.mirano')}
                    </span>
                  </div>
                  <h3 className="font-display text-sm text-[#F5F0E8]">{video.title}</h3>
                </div>
              </a>
            </motion.div>
          ))}
        </div>}

        {!loading && filteredVideos.length === 0 && (
          <div className="text-center py-20 text-[#B9B2A8]">
            <p>{t('videos.no_videos')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideosPage;